import { PlayerOptionsInterface } from '@cbsinteractive/avia-js';
import { VideoPlayerInterface } from '@cbsinteractive/avia-js/dist/types/';
import { IPromiseResolveGeneric } from 'library/ts/interfaces/IPromiseResolveGeneric';
import { SettingsVO } from 'library/ts/model/settings/SettingsVO';
import { AbstractTagClass } from 'tag/ts/tag/abstract/AbstractTagClass';
import { TagEvent } from 'tag/ts/tag/events/TagEvent';
import { TagEventVO } from 'tag/ts/tag/events/TagEventVO';
import { ITagEventDataVO } from 'tag/ts/tag/interfaces/events/ITagEventDataVO';
import { TagLoggerString } from 'tag/ts/tag/model/enums/TagLoggerString';
import { TagOnceSettingsProperties } from 'tag/ts/tag/model/enums/TagOnceSettingsProperties';
import { AviaFactory } from 'tag/ts/tag/player/adapters/avia/AviaFactory';
import { AviaPlayerWrapper } from 'tag/ts/tag/player/adapters/avia/AviaPlayerWrapper';
import { IAviaLifecycleManager } from 'tag/ts/tag/player/adapters/avia/managers/interfaces/IAviaLifecycleManager';
import { IAviaLifecycleManagerArgument } from 'tag/ts/tag/player/adapters/avia/managers/interfaces/IAviaLifecycleManagerArgument';
import { AviaPlayerOptionsManager } from 'tag/ts/tag/player/adapters/avia/resources/AviaPlayerOptionsManager';
import { INerdModeManager } from 'tag/ts/tag/interfaces/nerdmode/INerdModeManager';
import { TTagEventCallback } from 'tag/ts/tag/types/TTagEventCallback';

/**
 * @copyright © Paramount Streaming
 * @author Stan Gletski
 * @description Manages Avia player lifecycle including creation, destruction, and wrapper management
 *
 * **Responsibilities:**
 * - **Atomic Player Replacement**: Destroy old player, create new player with zero overlap
 * - **Wrapper Management**: Create and destroy AviaPlayerWrapper instances
 * - **Nerd Mode Integration**: Initialize/update Nerd Mode manager with player instances
 * - **Event Coordination**: Dispatch lifecycle events to Tag application
 * - **State Management**: Track player number and wrapper state
 *
 * **Atomic Operation Guarantee:**
 * Player replacement follows strict sequence:
 * 1. Delay (allow previous operations to settle)
 * 2. Destroy old player completely
 * 3. Destroy old wrapper completely
 * 4. Hide Nerd Mode (will update later)
 * 5. Create new player
 * 6. Create new wrapper
 * 7. Update Nerd Mode with new player
 * 8. Wait for event listeners ready
 * 9. Dispatch success event
 *
 * **Lifecycle:**
 * - Called when Tag dispatches ON_CREATE_PLAYER_REQUEST or ON_DESTROY_PLAYER_REQUEST
 * - Manages complete player lifecycle from creation to cleanup
 * - Ensures proper resource cleanup even on errors
 *
 * **Architecture:**
 * Extracted from AviaAdapter to follow Single Responsibility Principle.
 * Reduces AviaAdapter complexity by ~180 lines.
 * Centralizes all player lifecycle logic in one place.
 */
export class AviaLifecycleManager extends AbstractTagClass implements IAviaLifecycleManager {

	// Log message constants
	static readonly #LOG_DESTROY_WRAPPER = 'destroyAviaPlayerWrapper:';
	static readonly #LOG_DESTROY_PLAYER = 'destroyPlayer:';
	static readonly #LOG_INIT_NERD_MODE = 'initializeOrUpdateNerdMode:';
	static readonly #LOG_DESTROY_NERD_MODE = 'destroyNerdMode:';

	// Core dependencies (managed by parent AbstractTagClass: tag, logger)
	#aviaFactory: AviaFactory;
	#playerOptionsManager: AviaPlayerOptionsManager;

	// State management
	#aviaPlayerWrapper: AviaPlayerWrapper | null = null;
	#playerContainer: HTMLElement | null = null;
	#playerNumber: number = 0;

	// Concurrency guard — prevents two concurrent createPlayer() calls. VideoAPI's
	// pending slot is the primary defense; this is a secondary safety net.
	#isPlayerCreating: boolean = false;

	constructor( argument: IAviaLifecycleManagerArgument ) {
		super(
			{
				...argument,
				name: 'AviaLifecycleManager'
			}
		);

		this.#aviaFactory = argument.aviaFactory;
		this.#playerOptionsManager = argument.playerOptionsManager;
	}

	/**
	 * @description Gets current player wrapper instance
	 * @returns {AviaPlayerWrapper | undefined}
	 */
	getPlayerWrapper(): AviaPlayerWrapper | undefined {
		return this.#aviaPlayerWrapper;
	}

	/**
	 * @description Reads the Nerd Mode plugin instance off `PlayerManager.plugins` — the
	 * standard Tag-level plugin mechanism constructs it there (see `TagConfigNerdMode.ts`,
	 * `params.owner: 'PlayerManager'`), so this class no longer holds its own field. Returns
	 * `undefined` if the plugin was never triggered (bootstrap gate never fired) or hasn't
	 * finished loading yet — every call site here already tolerates that via optional
	 * chaining, same as the standard plugin mechanism's own "silently skipped" tolerance.
	 * @private
	 */
	#getNerdModeManager(): INerdModeManager | undefined {
		return this.tag?.controller?.playerManager?.plugins?.NerdMode?.instance;
	}

	/**
	 * @description Gets current player number
	 * @returns {number}
	 */
	getPlayerNumber(): number {
		return this.#playerNumber;
	}

	/**
	 * @description Routes to stream-swap or full atomic player replacement depending on whether
	 * the caller opted into avia instance reuse for this load via onceSettings.
	 * @param {HTMLElement} playerContainer - DOM container for player
	 * @returns {Promise<void>}
	 */
	createPlayer = async ( playerContainer: HTMLElement ): Promise<void> => {
		if ( this.#isPlayerCreating ) {
			this?.logger?.warn( 'createPlayer:', 'concurrent call rejected — creation already in flight' );
			return;
		}

		this.#isPlayerCreating = true;
		// Store player DOM container reference
		this.#playerContainer = playerContainer;
		this?.logger?.log( 'createPlayer:', 'player container:', this.#playerContainer );

		// Read-once: getOnceSettings deletes the key after reading, so this resets to undefined
		// on the next load() call unless the caller passes { reusePlayerInstance: true } again.
		const reusePlayerInstance = this.tag?.model?.onceSettingsManager?.getOnceSettings( TagOnceSettingsProperties.REUSE_PLAYER_INSTANCE ) as boolean;

		// canReuse requires an existing wrapper — first load always has none, so it always
		// falls through to #atomicPlayerReplacement() regardless of the flag value.
		const wrapperExists = this.utils.Type.isEmpty( this.#aviaPlayerWrapper ) === false;
		const canReuse = reusePlayerInstance === true && wrapperExists;

		if ( canReuse ) {
			await this.#streamSwapPath();
		} else {
			await this.#atomicPlayerReplacement();
		}
	};

	/**
	 * @description Swaps the active stream by reusing the existing avia-js player instance.
	 * Skips the full destroy/create cycle in #atomicPlayerReplacement(). To also preserve the
	 * `<video>` element and Chrome's established autoplay permission, the host must set
	 * `TAG.playerOptions.reuseVideoElement: true` at init — without it, avia-js recreates the
	 * element on every `attachResource()` and Chrome re-evaluates autoplay, causing muted playback.
	 *
	 * **How attachResource() is triggered:** This method dispatches ON_CREATE_PLAYER_SUCCESS
	 * without calling attachResource() directly. The downstream chain handles it:
	 * ON_CREATE_PLAYER_SUCCESS → SkinController → PlayerManager.loadResource()
	 *   → AviaAPITransformer.loadResource() → attachResource() on the existing player.
	 * This preserves the correct sequencing: Skin resets (panels close, CC hides) before
	 * the new stream starts loading — identical to the atomic path.
	 *
	 * **NerdMode:** hide only — no updatePlayerInstance() needed since the player instance
	 * is the same object. It will be shown again when the new resource starts.
	 *
	 * Falls back to #atomicPlayerReplacement() on error.
	 * @private
	 */
	#streamSwapPath = async (): Promise<void> => {
		const LOG_PREFIX = 'streamSwapPath:';
		try {
			this?.logger?.log( LOG_PREFIX, 'Reusing existing avia player instance...' );

			const nerdModeManager = this.#getNerdModeManager();
			if ( nerdModeManager ) {
				nerdModeManager.hide();
				// Delimit content sessions in the continuous capture buffer. hide() no longer
				// stops a background capture (?nerdcapture), so the marker lands between the
				// prior content's events and the reused instance's next stream.
				nerdModeManager.markCaptureBoundary();
				this?.logger?.log( LOG_PREFIX, 'Nerd mode hidden' );
			}

			await this.#reregisterStreamSwapPlugins();

			// Dispatching ON_CREATE_PLAYER_SUCCESS resets Skin state and triggers the
			// downstream loadResource() → attachResource() chain on the existing player.
			// Increment AFTER a successful dispatch so that on any error the fallback to
			// #atomicPlayerReplacement() increments exactly once — no double-increment,
			// no need to snapshot/restore.
			this.#dispatchTagEvent(
				new TagEventVO(
					TagEvent.ON_CREATE_PLAYER_SUCCESS,
					this.tag,
					{ playerNumber: this.#playerNumber + 1 } as ITagEventDataVO,
					this
				)
			);

			this.#playerNumber++;
			this?.logger?.log( LOG_PREFIX, 'Stream swap dispatched ON_CREATE_PLAYER_SUCCESS, playerNumber:', this.#playerNumber );
			this.#isPlayerCreating = false;
		} catch ( error ) {
			this?.logger?.warn( LOG_PREFIX, 'Stream swap failed, falling back to full replacement:', error );
			await this.#atomicPlayerReplacement();
			// #isPlayerCreating reset by #atomicPlayerReplacement's own success/failure path
		}
	};

	/**
	 * @description Re-registers every plugin that declares itself `isReinitializable()` (see
	 * `IAviaPlugin.isReinitializable`) on the reused avia-js player instance, so their configs
	 * and eligibility reflect the swapped-in resource's current tag params instead of staying
	 * pinned to whatever was set at the original player creation. Currently `AdsPlugin`
	 * (PLBCK-8461, ad-break/seek-lock enforcement) and `PalPlugin` (PLBCK-8475, consent/COPPA
	 * suppression + live-vs-VOD signal) opt in — the rest of the roster (ShakaPlugin/HlsPlugin,
	 * measurement plugins, etc.) simply doesn't declare itself reinitializable, since re-running
	 * those mid-swap risks losing session-scoped state this method has no way to know about.
	 * Which plugins participate is entirely a per-plugin decision — this method itself has no
	 * opinion on that, only on how to reconcile whichever ones opt in. No-op if the opted-in
	 * roster has nothing to register or remove.
	 *
	 * Handles both directions of eligibility change (PLBCK-8475): a plugin that's currently
	 * eligible gets removed-then-re-registered with a fresh config (same as before); a plugin
	 * that's opted in but no longer passes `shouldBeUsed()` (e.g. PAL suppression flipping on
	 * for a kids-content swap) gets explicitly removed instead of just silently dropping out of
	 * the config list with nothing to tear it down.
	 *
	 * Any error here propagates to `#streamSwapPath()`'s own try/catch, which already falls
	 * back to `#atomicPlayerReplacement()` — no additional error handling needed here.
	 *
	 * **Teardown guard:** `player.removePlugin()` returns `void` — avia-js gives no contract
	 * for when an old plugin instance actually finishes tearing down before we register the
	 * new one, which could in theory let an old plugin instance double-fire alongside its
	 * replacement (e.g. duplicate ad-break registration, double beacon fire). In the current
	 * avia-js/Blackbird implementation this is safe for both AdsPlugin and PalPlugin
	 * (`destroy()` is synchronous and unsubscribes from player events immediately), but that's
	 * an implementation detail, not a guarantee. For the add/refresh path, this throws rather
	 * than registering the replacement anyway if the old instance is still present — the throw
	 * propagates to this method's own caller and falls back to `#atomicPlayerReplacement()`
	 * (PR #4104 review, Daniel Silva / Nick Rickenbach). For pure removals there's no
	 * replacement pending for that id, so a lingering instance is only logged, not fatal.
	 * @private
	 */
	#reregisterStreamSwapPlugins = async (): Promise<void> => {
		const { toRegister, idsToRemove } = this.#playerOptionsManager.getReinitializablePluginDiff();
		if ( !toRegister.length && !idsToRemove.length ) {
			return;
		}

		const player = this.#aviaPlayerWrapper?.adaptedPlayerInstance;
		if ( !player ) {
			return;
		}

		for ( const id of idsToRemove ) {
			player.removePlugin( id );

			// No replacement is being registered for this id, so there's no double-registration
			// risk the way there is on the add/refresh path below — log if teardown hasn't
			// completed after the yield, but don't abort the whole reinit pass over it.
			await Promise.resolve();
			if ( player.getPlugin( id ) ) {
				this?.logger?.warn( 'reregisterStreamSwapPlugins:', 'Removed plugin still present after microtask yield (no replacement pending):', id );
			}
		}

		for ( const config of toRegister ) {
			player.removePlugin( config.id );

			// killPlugin() does `await plugin.destroy(); delete this.plugins[id];` — `await` always
			// defers its continuation by at least one microtask, so checking synchronously right
			// here would always show "still present" regardless of actual teardown state. Yield one
			// microtask first so this check reflects reality. Verified live against Pluto TV
			// (PLBCK-8461): resolves to false, confirming Blackbird's synchronous teardown holds.
			await Promise.resolve();
			if ( player.getPlugin( config.id ) ) {
				// Still present after the yield — registering the replacement now risks duplicate
				// ad-break registration or double beacon fire. Abort this reinit pass entirely and
				// let the caller's catch fall back to a full atomic player replacement instead.
				throw new Error( `reregisterStreamSwapPlugins: old plugin instance still present after microtask yield, aborting reinit for '${config.id}'` );
			}
		}

		if ( toRegister.length ) {
			await player.registerPlugins( toRegister );
			this?.logger?.log( 'reregisterStreamSwapPlugins:', 'Plugins re-registered for stream swap:', toRegister.map( ( config ) => config.id ) );
		}
	};

	/**
	 * @description Performs atomic player replacement (destroy old -> create new)
	 * @private
	 */
	#atomicPlayerReplacement = async (): Promise<void> => {
		const LOG_PREFIX = 'atomicPlayerReplacement:';

		try {
			this?.logger?.log( LOG_PREFIX, 'Starting atomic player replacement...' );

			// Wait for initial delay (using Tag utilities Delay utility)
			await this.utils.Delay.delay();

			// ATOMICALLY destroy old player (must complete before creating new one)
			await this.#aviaFactory.destroyPlayer( this.#aviaPlayerWrapper );
			this?.logger?.log( LOG_PREFIX, 'Player destroyed' );

			// ATOMICALLY destroy wrapper (must complete before creating new one)
			await this.#destroyAviaPlayerWrapper();
			this?.logger?.log( LOG_PREFIX, 'Wrapper destroyed' );

			// Don't destroy nerd mode manager - just hide it (will update player instance later)
			if ( this.#getNerdModeManager() ) {
				this.#getNerdModeManager()?.hide();
				this?.logger?.log( LOG_PREFIX, 'Nerd mode hidden for player replacement' );
			}

			// Get player options for new player
			const optionsData = await this.#playerOptionsManager.getPlayerOptions( this.#playerContainer );

			// Create new player (old player is guaranteed to be fully destroyed)
			const playerData = await this.#aviaFactory.createPlayer( optionsData.result as PlayerOptionsInterface );
			this?.logger?.log( TagLoggerString.EXECUTE_PLAYER_CREATE_EVENT, 'avia player created:', playerData?.player?.id );

			// PLBCK-7107 (STC-1365 derived): apply brand-specific launch defaults to the
			// fresh player instance. Pluto TV launches muted by default; only force mute on
			// the FIRST player creation in the session so a user who unmuted does not get
			// re-muted on subsequent VIDEO.load() calls. After this, the user's mute choice
			// propagates through the shared settings model and persists across reloads.
			this.#applyBrandLaunchDefaults( playerData?.player );

			// Create wrapper for new player
			this.#aviaPlayerWrapper = new AviaPlayerWrapper( { tag: this.tag, aviaPlayerInstance: playerData?.player } );
			this.#playerNumber++; // increment number of players created for video playback

			// Initialize or update nerd mode manager with new player instance
			await this.#initializeOrUpdateNerdMode( playerData?.player );

			// **CRITICAL**: Wait for event listeners to be registered before dispatching success
			// This prevents race condition where attachResource() is called before the wrapper
			// can receive RESOURCE_START events from avia-js
			await this.#aviaPlayerWrapper.waitForReady();
			this?.logger?.log( LOG_PREFIX, 'Event listeners ready' );

			// Inform application of success
			this?.logger?.log( LOG_PREFIX, 'Player replacement completed successfully' );
			this.#isPlayerCreating = false;
			this.#dispatchTagEvent(
				new TagEventVO(
					TagEvent.ON_CREATE_PLAYER_SUCCESS,
					this.tag,
					{ playerNumber: this.#playerNumber } as ITagEventDataVO,
					this
				)
			);

		} catch ( error ) {
			// Centralized error handling for entire atomic operation
			this?.logger?.warn( LOG_PREFIX, 'Player replacement failed:', error );
			this.#isPlayerCreating = false;
			this.#dispatchTagEvent(
				new TagEventVO(
					TagEvent.ON_CREATE_PLAYER_FAILURE,
					this.tag,
					{ playerNumber: this.#playerNumber } as ITagEventDataVO,
					this
				)
			);
		}
	};

	/**
	 * @description Applies brand-specific defaults to a freshly-created player instance.
	 * Currently scoped to PLBCK-7107 / STC-1365: Pluto TV launches muted by default in the
	 * web player container.
	 *
	 * **First-launch only:** gated on `#playerNumber === 0` so a user who unmutes is not
	 * re-muted on subsequent `tag.API.VIDEO.load()` calls in the same session. The settings
	 * model and the player's own muted state both carry the user's choice forward.
	 *
	 * **Why here (not AviaPlayerOptionsManager):** `PlayerOptionsInterface` (the type Avia
	 * accepts at `createPlayer` time) has no `muted` field — only `autoplay` policy enums.
	 * The fresh `VideoPlayerInterface` instance does expose `muted: boolean` directly, so
	 * we set it post-create as the documented launch default.
	 *
	 * **Anchored in `settings.mute` too (PR #3954 review #1, Daniel):** setting only
	 * `player.muted = true` is not durable — `SoundManager.#syncMute` reads `settings.mute`
	 * on `RESOURCE_START` and re-applies it (unless `autoplay.supportsUnmutedAutoplay === false`).
	 * On any environment where unmuted autoplay is allowed, the player.muted would get cleared
	 * back to `settings.mute === false` and defeat the STC-1365 AC. We persist `settings.mute`
	 * (the source of truth the rest of the volume stack reads) and let SoundManager reinforce
	 * the mute rather than fight it.
	 *
	 * @param player - The newly-created Avia player instance. No-op if undefined/null.
	 */
	#applyBrandLaunchDefaults = ( player: VideoPlayerInterface | undefined ): void => {
		if ( !player ) {
			return;
		}
		// Only apply on the first player creation in the session — subsequent VIDEO.load()
		// calls carry the user's mute choice forward through the shared settings model.
		if ( this.#playerNumber !== 0 ) {
			return;
		}
		// Opt-in per embed via tagConfig.Player.params.launchMuted. Defaults to false,
		// so no forced mute on any standard embed — host must explicitly request it
		// (e.g. mini player where silent autoplay is required).
		if ( !this.tagConfig?.Player?.params?.launchMuted ) {
			return;
		}

		try {
			player.muted = true;
			// Persist in `settings.mute` so SoundManager.#syncMute reinforces the mute on
			// RESOURCE_START instead of overriding it. `AbstractTagClass.get settings` returns
			// undefined when no localStorage entry exists yet (fresh session / cleared storage) —
			// falling back to a default SettingsVO() here is required, otherwise the persistence
			// write is skipped and the Skin-side settings getter (AbstractSkinClass.ts) creates
			// its own default (mute: false) on first read, which SoundManager then applies to
			// the player, silently un-muting it.
			const settings = this.settings ?? new SettingsVO();
			settings.mute = true;
			const key = this.localStorageName;
			if ( key ) {
				this.utils.Store.set( key, settings );
			}
			this?.logger?.log( 'applyBrandLaunchDefaults:', 'launchMuted — player.muted = true, settings.mute = true' );
		} catch ( error ) {
			// Defensive: don't fail player creation if the underlying setter throws.
			this?.logger?.warn( 'applyBrandLaunchDefaults:', 'failed to set launchMuted:', error );
		}
	};

	/**
	 * @description Cleans up and destroys avia player wrapper class instance
	 * @returns {Promise<IPromiseResolveGeneric>}
	 * @private
	 */
	#destroyAviaPlayerWrapper = (): Promise<IPromiseResolveGeneric> => {
		this?.logger?.log( AviaLifecycleManager.#LOG_DESTROY_WRAPPER, 'wrapper:', this.#aviaPlayerWrapper );

		return new Promise(
			( resolve, _reject ) => {
				const resolvePromise = (): void => {
					this?.logger?.log( AviaLifecycleManager.#LOG_DESTROY_WRAPPER, 'Done. Resolve promise' );
					resolve( { msg: 'Avia player wrapper destroyed' } );
				};

				// Check if wrapper exists using Tag utilities
				const isEmpty = this.utils.Type.isEmpty( this.#aviaPlayerWrapper );

				switch ( isEmpty ) {
					case false: // wrapper exists
						this.#aviaPlayerWrapper.cleanUp().then(
							( _data ) => {
								this.#aviaPlayerWrapper = null; // kill avia player wrapper instance
								resolvePromise();
							}
						).catch(
							( error ) => {
								this?.logger?.warn( AviaLifecycleManager.#LOG_DESTROY_WRAPPER, 'Error cleaning up wrapper:', error );
								this.#aviaPlayerWrapper = null; // still clear reference even if cleanup failed
								resolvePromise();
							}
						);
						break;
					case true: // wrapper is undefined
						this.#aviaPlayerWrapper = null;
						resolvePromise();
						break;
				}
			}
		);
	};

	/**
	 * @description Updates the Nerd Mode plugin instance with the current player instance.
	 * Nerd Mode itself is no longer constructed here — the standard Tag-level plugin
	 * mechanism (`PlayerManager`'s own `AbstractTagClass` constructor, gated on
	 * `TagConfigNerdMode.enabled`) fetches and constructs it independently, before any
	 * player instance exists. This is the one remaining piece only `AviaLifecycleManager`
	 * can provide: the live player reference, handed over via `updatePlayerInstance()` —
	 * the same method that already handled every subsequent player swap, now also the
	 * first call. A no-op if the plugin was never triggered or hasn't finished loading yet
	 * — same tolerance the standard plugin mechanism already has everywhere else.
	 * @param {VideoPlayerInterface} playerInstance - New player instance for event subscription
	 * @returns {Promise<IPromiseResolveGeneric>}
	 * @private
	 */
	#initializeOrUpdateNerdMode = async ( playerInstance: VideoPlayerInterface ): Promise<IPromiseResolveGeneric> => {
		try {
			const nerdModeManager = this.#getNerdModeManager();
			if ( !nerdModeManager ) {
				this?.logger?.log( AviaLifecycleManager.#LOG_INIT_NERD_MODE, 'Nerd Mode plugin not loaded (disabled, or still fetching) — skipping' );
				return { success: true };
			}

			nerdModeManager.updatePlayerInstance( playerInstance );
			this?.logger?.log( AviaLifecycleManager.#LOG_INIT_NERD_MODE, 'Nerd mode player instance updated' );

			return { success: true };
		} catch ( error ) {
			this?.logger?.error( AviaLifecycleManager.#LOG_INIT_NERD_MODE, 'Failed:', error );
			return { success: false, error };
		}
	};

	/**
	 * @description Tears down the Nerd Mode plugin instance, if loaded.
	 * @returns {Promise<IPromiseResolveGeneric>}
	 * @private
	 */
	#destroyNerdMode = async (): Promise<IPromiseResolveGeneric> => {
		try {
			const nerdModeManager = this.#getNerdModeManager();
			if ( nerdModeManager ) {
				await nerdModeManager.destroy();
				this?.logger?.log( AviaLifecycleManager.#LOG_DESTROY_NERD_MODE, 'Nerd mode manager destroyed' );
			}
			return { success: true };
		} catch ( error ) {
			this?.logger?.error( AviaLifecycleManager.#LOG_DESTROY_NERD_MODE, 'Failed to destroy nerd mode:', error );
			return { success: false, error };
		}
	};

	/**
	 * @description Destroys current player instance and cleans up all resources
	 * @param {TTagEventCallback} callback - Callback to execute after destruction
	 * @returns {Promise<void>}
	 */
	destroyPlayer = async ( callback: TTagEventCallback ): Promise<void> => {
		const informApplication = ( success: boolean ): void => {
			// sanity check (using Tag utilities)
			if ( !this.utils.Type.isBoolean( success ) ) {
				return;
			}

			// execute callback if it exists
			if ( this.utils.Type.isFunction( callback ) ) {
				callback( success );
			}

			// inform application
			this.#dispatchTagEvent(
				new TagEventVO(
					( success === true ? TagEvent.ON_DESTROY_PLAYER_SUCCESS : TagEvent.ON_DESTROY_PLAYER_FAILURE ),
					this.tag,
					{ playerNumber: this.#playerNumber } as ITagEventDataVO,
					this
				)
			);
		};

		// avia player wrapper destruction wrapped in a method
		const destroyAviaPlayerWrapper = (): void => {
			// destroy avia wrapper
			this.#destroyAviaPlayerWrapper().then(
				( _data ) => {
					this?.logger?.log( AviaLifecycleManager.#LOG_DESTROY_PLAYER, 'destroyAviaPlayerWrapper: success' );
					// Destroy nerd mode manager (keyboard listener + display)
					this.#destroyNerdMode().then(
						() => {
							this?.logger?.log( AviaLifecycleManager.#LOG_DESTROY_PLAYER, 'Nerd mode destroyed' );
							informApplication( true );
						}
					).catch(
						( error ) => {
							this?.logger?.warn( AviaLifecycleManager.#LOG_DESTROY_PLAYER, 'Nerd mode destruction failed:', error );
							informApplication( true ); // Still inform success since player/wrapper were destroyed
						}
					);
				}
			).catch(
				( error ) => {
					this?.logger?.warn( AviaLifecycleManager.#LOG_DESTROY_PLAYER, 'destroyAviaPlayerWrapper failed:', error );
					informApplication( false );
				}
			);
		};

		// IMPORTANT: Hide Nerd Mode BEFORE destroying player to avoid unsubscribing from destroyed player
		if ( this.#getNerdModeManager() ) {
			this.#getNerdModeManager()?.hide();
			this?.logger?.log( AviaLifecycleManager.#LOG_DESTROY_PLAYER, 'Nerd mode hidden before player destruction' );
		}

		// check avia player instance exist (using wrapper API)
		const isEmpty = this.utils.Type.isEmpty( this.#aviaPlayerWrapper?.API );

		switch ( isEmpty ) {
			case false: // player exists
				// destroy avia player instance
				this.#aviaFactory.destroyPlayer( this.#aviaPlayerWrapper ).then(
					( _data ) => {
						this?.logger?.log( AviaLifecycleManager.#LOG_DESTROY_PLAYER, 'destroyPlayer success', 'adaptedPlayerInstance:', this.#aviaPlayerWrapper?.adaptedPlayerInstance );
						// destroy avia wrapper
						destroyAviaPlayerWrapper();
					}
				).catch(
					( error ) => {
						this?.logger?.warn( AviaLifecycleManager.#LOG_DESTROY_PLAYER, 'destroyPlayer failure:', error );
						// Still attempt wrapper cleanup even if player destruction failed
						destroyAviaPlayerWrapper();
					}
				);
				break;
			case true: // avia player does not exist
				this?.logger?.log( AviaLifecycleManager.#LOG_DESTROY_PLAYER, 'player does not exist' );
				// just in case destroy avia wrapper
				destroyAviaPlayerWrapper();
				break;
		}
	};

	/**
	 * @description Dispatches Tag event using inherited dispatchTagEvent from AbstractTagClass
	 * @param {TagEventVO} event
	 * @private
	 */
	#dispatchTagEvent = ( event: TagEventVO ): void => {
		this.dispatchTagEvent( event );
	};

	/**
	 * @description Gets Nerd Mode manager instance (for external access)
	 * @returns {INerdModeManager | undefined}
	 */
	getNerdModeManager(): INerdModeManager | undefined {
		return this.#getNerdModeManager();
	}

}